import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import { DialogContent, Fade, SvgIcon } from '@mui/material'
import { ReactComponent as CloseIcon } from '../../../../assets/images/Close.svg'

import Dialog from '@mui/material/Dialog'
import React, { ComponentProps, useCallback, useMemo, useState } from 'react'
import useValueMessageDisplayName from '../../../../shared/hooks/useDisplayNames/variants/useValueMessageDisplayName'
import useActivitiesDisplayName from '../../../../shared/hooks/useDisplayNames/variants/useActivitiesDisplayName'
import useEvidenceDisplayName from '../../../../shared/hooks/useDisplayNames/variants/useEvidenceDisplayName'
import Tabs from '../../../../shared/components/Tabs/Tabs'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import { LinkedItems, LinkingItemsIds, LinkingItem, LinkingItemType } from '../../Linking.types'
import { LinkingProps } from '../../Linking'
import { useGetLinkableItemsQuery } from '../../../../shared/queries/linking/useGetLinkableItemsQuery'
import LinkableItemsModalList from './LinkableItemsModalList'
import useStrategicImperativeDisplayName from '../../../../shared/hooks/useDisplayNames/variants/useStrategicImperativeDisplayName'
import LoadingBackdrop from '../../../../shared/components/LoadingBackdrop/LoadingBackdrop'
import { useRefValue } from '../../../../shared/hooks/useRefValue'
import { useItemGrouping } from '../../../../shared/hooks/linking/useItemGrouping'
import { useIsMobileDevice } from '../../../../shared/hooks/useIsMobileDevice'

type LinkableItemsModalProps = Pick<LinkingProps, 'type'> & {
    selectedTabId: LinkingItem['type']
    onTabChange: (tabId: LinkingItem['type']) => void
    linkedItems: LinkedItems
    handleClose: () => void
    handleSave: (selectedIds: LinkingItemsIds) => void
    isSaving?: boolean
}

const LinkableItemsModal = ({
    handleClose,
    type,
    linkedItems,
    selectedTabId,
    onTabChange,
    handleSave,
    isSaving = false,
}: LinkableItemsModalProps) => {
    const getLinkableItemsQuery = useGetLinkableItemsQuery(type)

    const valueMessageDisplayName = useValueMessageDisplayName()
    const activityDisplayName = useActivitiesDisplayName()
    const evidenceDisplayName = useEvidenceDisplayName()
    const strategicImperativeDisplayName = useStrategicImperativeDisplayName()

    const isMobileDevice = useIsMobileDevice()

    const initialSelectedIds = useMemo((): LinkingItemsIds => {
        const linkedItemsKeys = Object.keys(linkedItems) as Array<keyof LinkingItemsIds>
        return linkedItemsKeys.reduce((acc, item) => {
            const linkedItemIds = {}

            linkedItems[item].forEach((item) => {
                if (!linkedItemIds[item.type]) linkedItemIds[item.type] = []
                linkedItemIds[item.type].push(item._id)

                if (item.children) {
                    item.children.forEach((child) => {
                        if (!linkedItemIds[child.type]) linkedItemIds[child.type] = []
                        linkedItemIds[child.type].push(child._id)
                    })
                }
            })

            return {
                ...acc,
                ...linkedItemIds,
            }
        }, {} as LinkingItemsIds)
    }, [linkedItems])

    const [selectedIds, setSelectedIds] = useState<LinkingItemsIds>(initialSelectedIds)
    const selectedIdsRef = useRefValue(selectedIds)

    const checkIfSelected = useCallback(
        (item: LinkingItem) => {
            return !!selectedIds[item.type]?.includes?.(item._id)
        },
        [selectedIds]
    )

    const onSelect = useCallback((item: LinkingItem, selected: boolean) => {
        if (selected) {
            const hasChildren = !!item.children

            if (hasChildren) {
                const children = item.children
                children.forEach((child) => onSelect(child, selected))
            }

            setSelectedIds((selectedIds) => ({
                ...selectedIds,
                [item.type]: [...(selectedIds[item.type] || []).filter((id) => id !== item._id)],
            }))
        } else {
            const hasParent = !!item.extras?.parent

            if (hasParent) {
                const parent = item.extras.parent as LinkingItem
                const isParentSelected = !!selectedIdsRef.current[parent.type]?.includes?.(parent._id)
                if (!isParentSelected) onSelect({ ...parent, children: null }, isParentSelected)
            }

            setSelectedIds((selectedIds) => ({
                ...selectedIds,
                [item.type]: [...(selectedIds[item.type] || []), item._id],
            }))
        }
    }, [])

    const evidenceItemsWithKeyEvidenceStatements = useMemo(() => {
        if (!getLinkableItemsQuery.data?.evidence) return []

        const evidenceItemsMap = getLinkableItemsQuery.data.evidence.reduce(
            (acc, evidenceItem) => {
                return {
                    ...acc,
                    [evidenceItem._id]: evidenceItem,
                }
            },
            {} as Record<LinkingItem['_id'], LinkingItem>
        )

        getLinkableItemsQuery.data.keyEvidenceStatement?.forEach((keyEvidenceStatementItem) => {
            if (!keyEvidenceStatementItem.extras.parent?._id) return
            const parentEvidence = { ...evidenceItemsMap[keyEvidenceStatementItem.extras.parent._id] }

            if (!parentEvidence.children) parentEvidence.children = []

            parentEvidence.children.push(keyEvidenceStatementItem)

            evidenceItemsMap[parentEvidence._id] = parentEvidence
        })

        return Object.values(evidenceItemsMap)
    }, [getLinkableItemsQuery.data])

    const linkableItems = useMemo(() => {
        const linkableItems = { ...getLinkableItemsQuery.data }

        if (linkableItems?.evidence) {
            linkableItems.evidence = evidenceItemsWithKeyEvidenceStatements
        }

        return linkableItems
    }, [evidenceItemsWithKeyEvidenceStatements])

    const valueMessageGroups = useItemGrouping(linkableItems?.valueMessage, 'valueMessage')
    const activityGroups = useItemGrouping(linkableItems?.activity, 'activity')

    const getTabLabelByLinkingType = useCallback(
        (linkingType: LinkingItemType) => {
            const numberOfLinks = selectedIds?.[linkingType]?.length || 0

            const formatTabLabel = (displayName: string) => {
                return `${displayName} (${numberOfLinks})`
            }

            switch (linkingType) {
                case 'valueMessage':
                    return formatTabLabel(valueMessageDisplayName.plural)
                case 'activity':
                    return formatTabLabel(activityDisplayName.plural)
                case 'evidence':
                    return formatTabLabel(evidenceDisplayName.plural)
                case 'strategicImperative':
                    return formatTabLabel(strategicImperativeDisplayName.plural)
                default:
                    return null
            }
        },
        [selectedIds]
    )

    const getTabViewByLinkingType = useCallback(
        (linkingType: LinkingItem['type']) => {
            const getView = () => {
                switch (linkingType) {
                    case 'valueMessage':
                        return (
                            <LinkableItemsModalList
                                items={linkableItems?.valueMessage}
                                groups={valueMessageGroups.groups}
                                checkIfSelected={checkIfSelected}
                                onSelect={onSelect}
                                itemDisplayName={valueMessageDisplayName}
                            />
                        )
                    case 'activity':
                        return (
                            <LinkableItemsModalList
                                items={linkableItems?.activity}
                                groups={activityGroups.groups}
                                onSelect={onSelect}
                                checkIfSelected={checkIfSelected}
                                itemDisplayName={activityDisplayName}
                            />
                        )
                    case 'evidence':
                        return (
                            <LinkableItemsModalList
                                items={linkableItems?.evidence}
                                onSelect={onSelect}
                                checkIfSelected={checkIfSelected}
                                itemDisplayName={evidenceDisplayName}
                                parentGroupName="Paper"
                                childrenGroupName="Key Evidence Statements"
                            />
                        )
                    case 'strategicImperative':
                        return (
                            <LinkableItemsModalList
                                items={linkableItems?.strategicImperative}
                                onSelect={onSelect}
                                checkIfSelected={checkIfSelected}
                                itemDisplayName={strategicImperativeDisplayName}
                            />
                        )

                    default:
                        return null
                }
            }

            return (
                <Fade in key={linkingType} timeout={800}>
                    <Stack flex={1}>{getView()}</Stack>
                </Fade>
            )
        },
        [linkableItems, checkIfSelected, valueMessageGroups.groups, activityGroups.groups]
    )

    const tabs = useMemo(
        (): ComponentProps<typeof Tabs>['tabs'] =>
            Object.keys(linkableItems || {})
                .map((linkingType: LinkingItem['type']) => ({
                    id: linkingType,
                    label: getTabLabelByLinkingType(linkingType),
                    view: getTabViewByLinkingType(linkingType),
                }))
                .filter((tab) => !!tab.label),
        [getTabViewByLinkingType]
    )

    const isGroupLoading = valueMessageGroups.isLoading || activityGroups.isLoading

    if (getLinkableItemsQuery.isLoading || isGroupLoading) {
        return <LoadingBackdrop loading title="Linked data" subtitle="Fetching Linked Data" />
    }

    const canSave =
        JSON.stringify(
            Object.values(initialSelectedIds)
                .flat()
                .sort((a, b) => a.localeCompare(b))
        ) !==
        JSON.stringify(
            Object.values(selectedIds)
                .flat()
                .sort((a, b) => a.localeCompare(b))
        )

    return (
        <Dialog
            open
            onClose={handleClose}
            fullWidth
            fullScreen={isMobileDevice}
            maxWidth={false}
            aria-labelledby="form-dialog-title"
            PaperProps={{
                sx: {
                    maxWidth: '720px',
                    py: 4,
                    px: isMobileDevice ? 4 : 8,
                    borderRadius: '24px',
                    boxShadow: '0px 15px 50px 0px rgba(0, 0, 0, 0.30)',
                },
            }}
        >
            <DialogTitle id="form-dialog-title" sx={{ p: 0 }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    pb={1.5}
                    borderBottom="1px solid"
                    borderColor="figma.grayscale.stroke"
                >
                    <Typography variant="22x600" color="figma.grayscale.textHier1">
                        Linked data
                    </Typography>
                    <IconButton
                        onClick={handleClose}
                        size="small"
                        sx={{
                            p: 0.5,
                            color: 'figma.grayscale.textHier2',
                        }}
                    >
                        <SvgIcon component={CloseIcon} inheritViewBox fontSize="medium" color="inherit" />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                <Stack direction="column" gap={1} pt={2} pb={1}>
                    <Tabs
                        orientation="horizontal"
                        tabs={tabs}
                        selectedTabId={selectedTabId}
                        onTabChange={onTabChange}
                        tabPanelSx={{ py: 3 }}
                        tabListWrapper={(tabList) => <Stack direction="row">{tabList}</Stack>}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Box flex={1} display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                        onClick={handleClose}
                        // @ts-expect-error mui type
                        variant="outlined-sm-button"
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleSave(selectedIds)}
                        disabled={!canSave || isSaving}
                        // @ts-expect-error mui type
                        variant="contained-sm-button"
                        color="primary"
                        sx={{
                            '&.Mui-disabled': {
                                color: 'figma.primary2',
                            },
                        }}
                    >
                        Save
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    )
}

export default LinkableItemsModal
